import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErr } from "@/lib/format";
import { XIcon, PlantIcon } from "@phosphor-icons/react";
import { useModalForm } from "@/lib/modalForm";
import BotonCerrar from "@/components/BotonCerrar";

/**
 * <ReceiveModal />
 * Modal de recepción de mercancía contra una Orden de Compra.
 * - Multi-bodega, batch, lote de fabricación, fechas condicionales (semilla vs producto).
 * - Llama a POST /api/purchase-orders/{oid}/receive
 * - Opcionalmente crea la factura de compra automáticamente al completar la OC.
 *
 * Props:
 *   po: { id, numero, proveedor_nombre, items:[{producto_id, codigo, descripcion, cantidad, cantidad_recibida, ...}] }
 *   onClose: () => void
 *   onSaved: () => void   // callback cuando la recepción se guarda
 */
export default function ReceiveModal({ po, onClose, onSaved }) {
  const [recv, setRecv] = useState(po.items.map((it) => ({
    producto_id: it.producto_id, codigo: it.codigo, descripcion: it.descripcion,
    descripcion_factura: it.descripcion || "",
    cantidad_solicitada: it.cantidad, cantidad_pendiente: it.cantidad - (it.cantidad_recibida || 0),
    cantidad_recibida: it.cantidad - (it.cantidad_recibida || 0),
    lote: "", lote_fabricacion: "", fecha_test: "", fecha_fabricacion: "", fecha_vencimiento: "",
    fecha_ingreso: new Date().toISOString().slice(0, 10),
    bodega_id: "", bodega_codigo: "", es_semilla: false, ubicacion: "",
  })));
  const [numProv, setNumProv] = useState("");
  const [caiProv, setCaiProv] = useState("");
  const [crearFactura, setCrearFactura] = useState(true);
  const [warehouses, setWarehouses] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: whs }, { data: prods }] = await Promise.all([
          api.get("/warehouses", { params: { activo: true } }),
          api.get("/products"),
        ]);
        const usable = whs.filter((w) => w.tipo !== "transito");
        setWarehouses(usable);
        const principal = usable.find((w) => w.tipo === "principal");
        setRecv((prev) => prev.map((r) => {
          const p = prods.find((x) => x.id === r.producto_id);
          return {
            ...r,
            es_semilla: !!(p && p.es_semilla),
            // Si el producto ya tiene una descripción en catálogo, precargala para editar en el ingreso
            descripcion_factura: r.descripcion_factura || (p && p.descripcion) || r.descripcion || "",
            bodega_id: principal ? principal.id : r.bodega_id,
            bodega_codigo: principal ? principal.codigo : r.bodega_codigo,
          };
        }));
      } catch { /* tolerable */ }
    })();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const items = recv.filter((r) => Number(r.cantidad_recibida) > 0).map((r) => {
        const wh = warehouses.find((w) => w.id === r.bodega_id);
        return {
          producto_id: r.producto_id,
          cantidad_recibida: Number(r.cantidad_recibida),
          lote: r.lote || "",
          lote_fabricacion: r.lote_fabricacion || "",
          fecha_test: r.fecha_test || null,
          fecha_fabricacion: r.fecha_fabricacion || null,
          fecha_vencimiento: r.fecha_vencimiento || null,
          fecha_ingreso: r.fecha_ingreso || null,
          bodega_id: r.bodega_id || null,
          bodega_codigo: wh ? wh.codigo : "",
          descripcion: r.descripcion_factura || "",
        };
      });
      if (items.length === 0) {
        toast.error("Debes recibir al menos 1 unidad");
        return;
      }
      await api.post(`/purchase-orders/${po.id}/receive`, {
        items, numero_proveedor: numProv, cai_proveedor: caiProv, crear_factura: crearFactura,
      });
      // Ubicación física por lote (se asigna a los ingresos recién creados de esta OC)
      const ubic = {};
      recv.forEach((r) => { if (r.ubicacion && Number(r.cantidad_recibida) > 0) ubic[r.producto_id] = r.ubicacion; });
      if (Object.keys(ubic).length) {
        await api.post(`/inventory/receipts/${encodeURIComponent(po.numero)}/ubicaciones`, { ubicaciones: ubic }).catch(() => {});
      }
      toast.success("Recepción registrada · inventario actualizado.");
      onSaved && onSaved();
    } catch (er) { toast.error(apiErr(er)); }
  };

  const rm = useModalForm({ value: { recv, numProv, caiProv, crearFactura }, onClose: onClose, onSave: submit });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...rm.overlayProps}>
      <div className="bg-card border border-border rounded-lg w-full max-w-5xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="receive-modal">
        <div className="p-5 border-b border-border flex justify-between sticky top-0 bg-card z-10">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-agro-yellow">Recepción de mercancía · Multi-bodega</div>
            <h3 className="font-heading text-lg font-semibold">{po.numero} · {po.proveedor_nombre}</h3>
            <div className="text-xs text-text-muted mt-1">Captura lote, fecha de test (semillas), vencimiento y bodega destino para trazabilidad PEPS.</div>
          </div>
          <button aria-label="Cerrar" onClick={rm.cerrar} className="text-text-muted" data-testid="receive-close"><XIcon size={20} /></button>
        </div>
        <form onSubmit={rm.guardar} className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">N° factura proveedor</label>
              <input value={numProv} onChange={(e) => setNumProv(e.target.value)} placeholder="000-001-01-XXXX" data-testid="receive-num-prov" className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1.5 block">CAI proveedor</label>
              <input value={caiProv} onChange={(e) => setCaiProv(e.target.value)} className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm focus:border-agro-green" />
            </div>
          </div>
          <div className="overflow-x-auto border border-border/60 rounded-md">
            <table className="w-full">
              <thead className="bg-sidebar">
                <tr className="text-left text-[10px] uppercase tracking-wider text-text-muted">
                  <th className="p-2 pl-4">Producto</th>
                  <th className="p-2 text-right">Pend.</th>
                  <th className="p-2 text-right">Recibir</th>
                  <th className="p-2">Bodega destino</th>
                  <th className="p-2">Ubicación</th>
                  <th className="p-2">Batch</th>
                  <th className="p-2">Lote fabricación</th>
                  <th className="p-2" title="Fecha del ingreso — determina el orden PEPS al facturar">F. Ingreso</th>
                  <th className="p-2" title="Solo aplica a semillas">F. Test</th>
                  <th className="p-2" title="Solo aplica a productos (no semillas)">F. Fab.</th>
                  <th className="p-2 pr-4" title="Solo aplica a productos (no semillas)">F. Vence</th>
                </tr>
              </thead>
              <tbody>
                {recv.map((r, i) => (
                  <tr key={r.producto_id} className="border-t border-border/40 align-top" data-testid={`receive-row-${r.codigo}`}>
                    <td className="p-2 pl-4 text-xs">
                      <div className="font-mono text-agro-green flex items-center gap-1">
                        {r.codigo}
                        {r.es_semilla && <span className="text-agro-yellow text-[10px] inline-flex items-center gap-0.5" title="Semilla"><PlantIcon size={12} aria-hidden="true" /> Semilla</span>}
                      </div>
                      <input
                        type="text"
                        value={r.descripcion_factura}
                        onChange={(e) => { const n = [...recv]; n[i].descripcion_factura = e.target.value; setRecv(n); }}
                        placeholder="Descripción para factura…"
                        title="Descripción del producto que aparecerá en la factura de venta"
                        data-testid={`receive-descripcion-${r.codigo}`}
                        className="mt-1 w-[220px] bg-app border border-border/60 rounded px-2 py-1 text-[11px] text-text-secondary focus:border-agro-yellow"
                      />
                    </td>
                    <td className="p-2 text-right text-xs text-agro-yellow">{r.cantidad_pendiente}</td>
                    <td className="p-2">
                      <input type="number" value={r.cantidad_recibida} max={r.cantidad_pendiente} min={0}
                             data-testid={`receive-qty-${r.codigo}`}
                             onChange={(e) => { const n = [...recv]; n[i].cantidad_recibida = e.target.value; setRecv(n); }}
                             className="w-20 bg-app border border-border rounded px-2 py-1.5 text-xs text-right focus:border-agro-green" />
                    </td>
                    <td className="p-2">
                      <select value={r.bodega_id} data-testid={`receive-bodega-${r.codigo}`}
                              onChange={(e) => { const n = [...recv]; n[i].bodega_id = e.target.value; setRecv(n); }}
                              className="w-36 bg-app border border-border rounded px-2 py-1.5 text-xs focus:border-agro-green">
                        <option value="">—</option>
                        {warehouses.map((w) => <option key={w.id} value={w.id}>{w.codigo}</option>)}
                      </select>
                    </td>
                    <td className="p-2">
                      <input type="text" value={r.ubicacion} placeholder="P3-E2" data-testid={`receive-ubicacion-${r.codigo}`}
                             onChange={(e) => { const n = [...recv]; n[i].ubicacion = e.target.value; setRecv(n); }}
                             className="w-20 bg-app border border-border rounded px-2 py-1.5 text-xs focus:border-agro-green" />
                    </td>
                    <td className="p-2">
                      <input type="text" value={r.lote} placeholder="B-001" data-testid={`receive-lote-${r.codigo}`}
                             onChange={(e) => { const n = [...recv]; n[i].lote = e.target.value; setRecv(n); }}
                             className="w-24 bg-app border border-border rounded px-2 py-1.5 text-xs focus:border-agro-green" />
                    </td>
                    <td className="p-2">
                      <input type="text" value={r.lote_fabricacion} placeholder="LF-2026-001" data-testid={`receive-lote-fab-${r.codigo}`}
                             onChange={(e) => { const n = [...recv]; n[i].lote_fabricacion = e.target.value; setRecv(n); }}
                             className="w-28 bg-app border border-border rounded px-2 py-1.5 text-xs focus:border-agro-green"
                             title="Lote de fabricación que viene impreso de fábrica/proveedor" />
                    </td>
                    <td className="p-2">
                      <input type="date" value={r.fecha_ingreso} data-testid={`receive-fecha-ing-${r.codigo}`}
                             onChange={(e) => { const n = [...recv]; n[i].fecha_ingreso = e.target.value; setRecv(n); }}
                             className="w-36 bg-app border border-border rounded px-2 py-1.5 text-xs focus:border-agro-green"
                             title="Fecha del ingreso al inventario (define el orden PEPS al facturar)" />
                    </td>
                    <td className="p-2">
                      <input type="date" value={r.fecha_test} disabled={!r.es_semilla} data-testid={`receive-fecha-test-${r.codigo}`}
                             onChange={(e) => { const n = [...recv]; n[i].fecha_test = e.target.value; setRecv(n); }}
                             className={`w-36 bg-app border border-border rounded px-2 py-1.5 text-xs focus:border-agro-green ${!r.es_semilla ? "opacity-30 cursor-not-allowed" : ""}`}
                             title={r.es_semilla ? "Fecha de test de germinación (PEPS)" : "Solo semillas"} />
                    </td>
                    <td className="p-2">
                      <input type="date" value={r.fecha_fabricacion} disabled={r.es_semilla} data-testid={`receive-fecha-fab-${r.codigo}`}
                             onChange={(e) => { const n = [...recv]; n[i].fecha_fabricacion = e.target.value; setRecv(n); }}
                             className={`w-36 bg-app border border-border rounded px-2 py-1.5 text-xs focus:border-agro-green ${r.es_semilla ? "opacity-30 cursor-not-allowed" : ""}`}
                             title={r.es_semilla ? "Solo productos (no semillas)" : "Fecha de fabricación del lote"} />
                    </td>
                    <td className="p-2 pr-4">
                      <input type="date" value={r.fecha_vencimiento} disabled={r.es_semilla} data-testid={`receive-vence-${r.codigo}`}
                             onChange={(e) => { const n = [...recv]; n[i].fecha_vencimiento = e.target.value; setRecv(n); }}
                             className={`w-36 bg-app border border-border rounded px-2 py-1.5 text-xs focus:border-agro-green ${r.es_semilla ? "opacity-30 cursor-not-allowed" : ""}`}
                             title={r.es_semilla ? "Solo productos (no semillas)" : "Fecha de vencimiento del lote"} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center gap-2">
            <input id="crear-fact-rm" type="checkbox" checked={crearFactura} onChange={(e) => setCrearFactura(e.target.checked)} className="w-4 h-4 accent-agro-green" data-testid="receive-crear-factura" />
            <label htmlFor="crear-fact-rm" className="text-sm">Crear factura automáticamente al recibir todo</label>
          </div>
          <button disabled={rm.saving} type="submit" data-testid="receive-submit" className="w-full bg-agro-green hover:bg-agro-green-hover text-black font-semibold rounded-md py-3">Confirmar recepción</button>
        </form>
      </div>
    </div>
  );
}
